import React, { useContext, useEffect, useState } from "react";
import Layout from "../../layouts";
import { Box, Button, Typography, Grid } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DashboardContext } from "../../contexts/DashboardContext";
import { useTranslation } from "react-i18next";
import { EmissionData } from "../../models/Emission";
import dayjs, { Dayjs } from "dayjs";
import { Outlet } from "react-router-dom";
import apiClient from "../../api/axios";
import alertService from "../alert/alertService";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import CustomDatePicker from "../input/CustomDatePicker";
import { common, overview } from "../../i18n";
import { TabMenu, TabMenuData } from "../tabMenu/TabMenu";
import { isScope3Open } from "../../config/config";
import CustomModal from "../modal/CustomModal";
import { PreviewExport } from "../../pages/dashboard/preview/PreviewExport";
import CardReceive from "../icon/CardReceive";
import { MouseOverPopover } from "../popper/MouseOverPopover";
import { ApiResponse } from "../../models/api-responses.types";
import { DashboardResults } from "../../models/emission-dashboard.types";
import { useForm } from "react-hook-form";
import { MultipleAutoComplete } from "../input/MutipleAutoComplete";
import { MenuData } from "../input/CustomSelect";
import ArrayUtils from "../../utils/arrayUtils";

const tabMenu: TabMenuData[] = [
  { pathname: "/overview/total-emissions", name: "Total-emissions" },
  { pathname: "/overview/scope-1", name: "Scope-1-emissions" },
  { pathname: "/overview/scope-2", name: "Scope-2-emissions" },
  { pathname: "/overview/scope-3", name: "Scope-3-emissions" },
];

const DashboardContent = () => {
  const orgContext = useContext(OrganizationContext);
  const subOrganizations = orgContext?.subOrganizations ?? [];

  const [emissions, setEmissions] = useState<EmissionData[]>([]);
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(() =>
    dayjs().startOf("year"),
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(() =>
    dayjs().endOf("year"),
  );
  const [dashboardData, setDashboardData] = useState<
    DashboardResults | undefined
  >(undefined);
  const [openExport, setOpenExport] = useState<boolean>(false);

  // 🌟 1. สร้าง State สำหรับรับรู้ว่าลูก (PreviewExport) กำลังทำงานอยู่หรือไม่
  const [isExportBusy, setIsExportBusy] = useState<boolean>(false);

  const { t } = useTranslation([common, overview], { nsMode: "fallback" });

  const { control, watch, setValue } = useForm({
    defaultValues: { subOrganizations: [] as string[] },
  });

  const selectSubOrganizations = watch("subOrganizations");

  // selectSubOrganizations holds ids (storeValue) — emissions only carry the
  // sub-org's display name, so resolve ids back to names for this filter.
  const selectedSubOrgNames = subOrganizations
    .filter((item) => selectSubOrganizations.includes(item.id))
    .map((item) => item.name);

  const filteredSubOrganizationEmssions =
    isScope3Open
      ? emissions.filter(
          (item) =>
            item.suborganizationName !== undefined &&
            selectedSubOrgNames.includes(item.suborganizationName),
        )
      : emissions.filter(
          (item) =>
            item.suborganizationName !== undefined &&
            selectedSubOrgNames.includes(item.suborganizationName) &&
            item.scope !== "Scope-3",
        );

  const filteredEmissions = filteredSubOrganizationEmssions.filter(
    (item) =>
      item.date &&
      startDate.valueOf() <= item.date &&
      item.date <= endDate.valueOf(),
  );

  const context = {
    emissions: filteredSubOrganizationEmssions,
    filteredEmissions,
    startDate,
    endDate,
    dashboardData,
  };

  const organizationOptions: MenuData[] = ArrayUtils.convertArrayToMenuData(
    "id",
    "name",
    "name",
    subOrganizations,
  );

  const pickStartDate = (day: dayjs.Dayjs) => {
    setStartDate(day.startOf("year"));
    setEndDate(day.endOf("year"));
  };

  const pickEndDate = (day: dayjs.Dayjs) => {
    const endYear = day.endOf("year");
    setEndDate(endYear);
    if (startDate.diff(endYear) > 0) setStartDate(day.startOf("year"));
  };

  const onDefaultClick = () => {
    setValue(
      "subOrganizations",
      subOrganizations.map((item) => item.id),
    );
    setStartDate(dayjs().startOf("year"));
    setEndDate(dayjs().endOf("year"));
  };

  const getEmissionV2 = async () => {
    try {
      alertService.showLoading();
      const response = await apiClient.post<ApiResponse<DashboardResults>>(
        "/v2/emissions/dashboard",
        {
          startDate: `${startDate.year()}-01-01T00:00:00Z`,
          endDate: `${endDate.year()}-12-31T23:59:59Z`,
          suborganizations: selectSubOrganizations,
        },
      );
      alertService.closeLoading();
      setDashboardData(response.data.results);
    } catch (error: any) {}
  };

  useEffect(() => {
    if (subOrganizations.length > 0) {
      setValue(
        "subOrganizations",
        subOrganizations.map((item) => item.id),
      );
    }
  }, [subOrganizations, setValue]);

  useEffect(() => {
    if (selectSubOrganizations && selectSubOrganizations.length > 0) {
      getEmissionV2();
    } else {
      setDashboardData(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectSubOrganizations, startDate, endDate]);

  // 🌟 2. ฟังก์ชันปิด Modal (เช็คความชัวร์ก่อนว่าระบบไม่ได้โหลดอยู่)
  const handleCloseExport = () => {
    if (!isExportBusy) {
      setOpenExport(false);
    }
  };

  return (
    <DashboardContext.Provider value={context}>
      {/* Filter */}
      <Box
        marginX={"40px"}
        p={"12px 40px"}
        display={"flex"}
        flexDirection={{ xs: "column", xl: "row" }}
        bgcolor={"#FAFAFA"}
        gap={"22px"}
        alignItems={"start"}
      >
        <Box
          width={"100%"}
          maxWidth={"100%"}
          display={"flex"}
          flexDirection={{ xs: "column", lg: "row" }}
          gap={"22px"}
        >
          <Grid item flex={1}>
            <MultipleAutoComplete
              control={control}
              name="subOrganizations"
              label={t("Sub-Organizations")}
              info={t("Sub-Organizations")}
              placeholder={t("Sub-Organizations")}
              listMenuItem={organizationOptions}
              showLabelInput={false}
              storeValue
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={"auto"}
            color={"var(--dark)"}
            display={"flex"}
            gap={"22px"}
            alignItems={"center"}
            width={"100%"}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <Box display={"flex"} alignItems={"center"} gap={"8px"}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CustomDatePicker
                  value={dayjs(startDate)}
                  defaultDate={dayjs(`${dayjs().year()}-01-01`)}
                  label={t("Start-Year")}
                  info={t("Start-Year")}
                  required
                  handleOnChange={(value: Dayjs) =>
                    value && pickStartDate(value)
                  }
                  views={["year"]}
                  showInfo={false}
                  format="YYYY"
                />
                <Typography>{t("To")}</Typography>
                <CustomDatePicker
                  value={dayjs(endDate)}
                  defaultDate={dayjs(`${dayjs().year()}-01-01`)}
                  label={t("End-Year")}
                  info={t("End-Year")}
                  required
                  handleOnChange={(value: Dayjs) => value && pickEndDate(value)}
                  views={["year"]}
                  showInfo={false}
                  format="YYYY"
                />
              </LocalizationProvider>
            </Box>
            <Button
              variant="text"
              color="green"
              onClick={onDefaultClick}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {t("Default")}
            </Button>
            <MouseOverPopover
              children={
                <Button
                  variant="contained"
                  color="green"
                  onClick={() => setOpenExport(true)}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  <CardReceive />
                </Button>
              }
              text={t("Download")}
            />
          </Grid>
        </Box>
      </Box>

      {/* Menu */}
      <TabMenu tabMenuData={tabMenu} />

      <CustomModal
        open={openExport}
        // 🌟 3. บังคับล็อกห้ามปิดถ้าระบบยังดาวน์โหลดไม่เสร็จ
        onClose={handleCloseExport}
        header={
          <Typography variant="h1">
            {t("Preview")} / {t("Total-emissions")}
          </Typography>
        }
        body={
          <PreviewExport
            // 🌟 4. ผูกสถานะ และสั่งให้ปิด Modal อัตโนมัติเมื่อดาวน์โหลดเสร็จ
            onLoading={(status) => setIsExportBusy(status)}
            onSuccess={() => setOpenExport(false)}
          />
        }
      />

      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"24px"}
        m={"12px 40px"}
      >
        <Outlet />
      </Box>
    </DashboardContext.Provider>
  );
};

const DashboardLayout = () => (
  <Layout>
    <DashboardContent />
  </Layout>
);

export default DashboardLayout;
